import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Coco } from "../../components/States";
import { api } from "../../data";
import { useAccount, useTalentWorkspace } from "../../app/session";
import { savePreferences } from "./actions";
import { useLang, useT } from "./sprache";
import { guideFor, guideText, type FirstStep, type GuideKey } from "./einstieg";
import { ereignisArgs, fortsetzSchritt, lugtStoert, rundgangEnde, rundgangJetzt, rundgangPatch, rundgangSchritt, rundgangStarten, sollAutoStarten, stationenFuer, useRundgang, type RundgangRolle, type RundgangStand } from "../agentur/coco-rundgang";
import { cocoUiText, stationText } from "../agentur/coco-rundgang-texte";
import { CocoLugt, RundgangBlase } from "../../components/CocoRundgang";

/**
 * Einstieg für neue Creator (Befund 7): kurze Einführung je Seite beim ersten Besuch (abschaltbar) und die Checkliste
 * „Erste Schritte“ in der Übersicht. Gemerkt wird beides in user_preferences.onboarding, damit es auf jedem Gerät gilt;
 * zusätzlich im Browser, damit eine Karte nach dem Wegklicken auch ohne Speichern (Demo, Netzfehler) weg bleibt.
 */

export interface Onboarding {
  intro_off?: boolean;
  guides?: Partial<Record<GuideKey, boolean>>;
  first_steps_dismissed?: boolean;
  /** Coco (docs/COCO.md, Erfolge): Checkliste war offen bzw. der Abschluss wurde schon gefeiert */
  first_steps_open?: boolean;
  first_steps_celebrated?: boolean;
  tax_hint_dismissed?: boolean;
  /** Rundgang mit Coco je Rolle (pages/agentur/coco-rundgang.ts): Stand, gezeigte Stationen, beendet oder übersprungen */
  rundgang?: Partial<Record<RundgangRolle, RundgangStand>>;
  [key: string]: unknown;
}

interface PrefsRow {
  user_id: string;
  onboarding: Onboarding | null;
}

const localKey = (userId: string) => `tk-einfuehrung:${userId}`;
function readLocal(userId: string | null): Onboarding {
  if (!userId) return {};
  try {
    return JSON.parse(localStorage.getItem(localKey(userId)) ?? "{}") as Onboarding;
  } catch {
    return {};
  }
}
function writeLocal(userId: string, o: Onboarding) {
  try {
    localStorage.setItem(localKey(userId), JSON.stringify(o));
  } catch {
    /* ohne Speicher gilt nur die Datenbank */
  }
}

/** Einstellungen der Einführung: Datenbank und Browser zusammengeführt; speichern schreibt beide. */
export function useOnboardingPrefs(userId: string | null) {
  const qc = useQueryClient();
  const prefs = useQuery({
    queryKey: ["creator-onboarding", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.select<PrefsRow>("user_preferences", { columns: "user_id, onboarding", filters: [["user_id", "eq", userId!]] }))[0] ?? null,
  });
  const local = readLocal(userId);
  const db = prefs.data?.onboarding ?? {};
  const merged: Onboarding = {
    ...db,
    ...local,
    guides: { ...(db.guides ?? {}), ...(local.guides ?? {}) },
    rundgang: { ...(db.rundgang ?? {}), ...(local.rundgang ?? {}) },
    intro_off: !!(db.intro_off || local.intro_off),
    first_steps_dismissed: !!(db.first_steps_dismissed || local.first_steps_dismissed),
  };
  async function save(patch: Onboarding) {
    if (!userId) return;
    // Zurücksetzen (guides: {}) gilt auch im Browser; sonst wird ergänzt
    const reset = !!patch.guides && Object.keys(patch.guides).length === 0;
    const nextLocal: Onboarding = reset ? { ...patch } : { ...local, ...patch, guides: { ...(local.guides ?? {}), ...(patch.guides ?? {}) }, rundgang: { ...(local.rundgang ?? {}), ...(patch.rundgang ?? {}) } };
    writeLocal(userId, nextLocal);
    qc.setQueryData(["creator-onboarding", userId], { user_id: userId, onboarding: { ...db, ...patch } });
    // Frisch lesen und zusammenführen: andere Hinweise (z. B. Steuerhinweis) schreiben in dasselbe Feld
    const current = (await api.select<PrefsRow>("user_preferences", { columns: "user_id, onboarding", filters: [["user_id", "eq", userId]] }))[0] ?? null;
    const base = current?.onboarding ?? {};
    const nextDb: Onboarding = reset ? { ...base, ...patch } : { ...base, ...patch, guides: { ...(base.guides ?? {}), ...(patch.guides ?? {}) }, rundgang: { ...(base.rundgang ?? {}), ...(patch.rundgang ?? {}) } };
    await savePreferences(api, userId, { onboarding: nextDb }, !!current);
    await qc.invalidateQueries({ queryKey: ["creator-onboarding", userId] });
  }
  return { onboarding: merged, ready: prefs.isSuccess, save };
}

/** Karte „So funktioniert diese Seite“ beim ersten Besuch; unten rechts, ohne den Inhalt zu sperren. */
export function SeitenEinfuehrung() {
  const { pathname } = useLocation();
  const ws = useTalentWorkspace();
  const { account, session } = useAccount();
  const userId = account?.session.userId ?? session?.userId ?? null;
  const lang = useLang();
  const t = useT();
  const titleId = useId();
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const [hidden, setHidden] = useState<GuideKey | null>(null);
  const [offen, setOffen] = useState<GuideKey | null>(null);
  const lauf = useRundgang();
  const key = guideFor(pathname);
  const embedded = typeof document !== "undefined" && document.documentElement.dataset.embed === "true";
  // Rundgang 20.10.2026: während des Rundgangs keine Karte; danach holt das Coco-Symbol sie jederzeit zurück
  const grundsaetzlich = !!key && ready && !embedded && !onboarding.intro_off && !lauf;
  const show = grundsaetzlich && (offen === key || (!onboarding.guides?.[key!] && hidden !== key));
  useEffect(() => setOffen(null), [pathname]);
  // CREATOR-9: die Karte liegt fest über der Seite; solange sie offen ist, hält der Inhalt Platz für sie frei
  // (breit: rechts daneben, schmaler: unten zum Hochscrollen), damit sie keine Summen und Felder verdeckt
  const card = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!show) return;
    const root = document.documentElement;
    root.classList.add("tk-guide-open");
    const h = card.current?.getBoundingClientRect().height;
    if (h) root.style.setProperty("--tk-guide-h", `${Math.ceil(h)}px`);
    // Nachtest CO-011: zwischen 768 und 1599 px liegt die Karte oben rechts; sie beginnt unter dem Seitenkopf, damit dessen
    // Hauptaktionen (etwa „Vertrag erstellen“) frei bleiben (kompakt.css, --tk-guide-top)
    const kopf = document.querySelector("main .pagehead")?.getBoundingClientRect();
    if (kopf) root.style.setProperty("--tk-guide-top", `${Math.ceil(kopf.bottom + window.scrollY + 12)}px`);
    return () => {
      root.classList.remove("tk-guide-open");
      root.style.removeProperty("--tk-guide-h");
      root.style.removeProperty("--tk-guide-top");
    };
  });
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && key) {
        setHidden(key);
        setOffen(null);
        void save({ guides: { [key]: true } }).catch(() => undefined);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  if (!grundsaetzlich || !key) return null;
  if (!show) return <CocoLugt label={cocoUiText(t).lugt} onClick={() => setOffen(key)} stoertAmHandy={lugtStoert(pathname)} />;
  const g = guideText(key, lang, ws.org.name);
  const done = (off: boolean) => {
    setHidden(key);
    setOffen(null);
    void save(off ? { intro_off: true } : { guides: { [key]: true } }).catch(() => undefined);
  };
  return (
    <aside ref={card} className="tk-guide panel stack" role="dialog" aria-modal="false" aria-labelledby={titleId}>
      <div className="tk-guide-kopf">
        <Coco pose="erklaert" size={56} eager />
        <div className="stack">
          <div className="eyebrow">{t("Coco erklärt", "Coco explains")}</div>
          <h2 id={titleId}>{g.title}</h2>
        </div>
      </div>
      <p>{g.text}</p>
      <ul>{g.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
      <div className="row">
        <button type="button" className="primary" onClick={() => done(false)}>{t("Verstanden", "Got it")}</button>
        <button type="button" onClick={() => done(true)}>{t("Einführung ausschalten", "Switch off introduction")}</button>
      </div>
      <small>{t("Wieder einschalten kannst du sie unter „Konto“.", "You can switch it back on under “Account”.")}</small>
    </aside>
  );
}

/** Checkliste „Erste Schritte“ (x von y erledigt); verschwindet, wenn alles erledigt oder ausgeblendet ist. */
export function ErsteSchritte({ steps, userId }: { steps: FirstStep[]; userId: string | null }) {
  const t = useT();
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const [gone, setGone] = useState(false);
  const done = steps.filter((s) => s.done).length;
  const offen = ready && !onboarding.first_steps_dismissed && done < steps.length;
  useEffect(() => {
    if (offen && !onboarding.first_steps_open) void save({ first_steps_open: true }).catch(() => undefined);
  }, [offen, onboarding.first_steps_open]);
  // Geschafft: Coco freut sich einmal, wenn die zuvor offene Checkliste vollständig ist (nicht bei Altbestand)
  if (ready && !gone && done === steps.length && onboarding.first_steps_open && !onboarding.first_steps_celebrated) {
    return (
      <section className="panel stack tk-first-steps" aria-labelledby="erste-schritte-fertig">
        <div className="tk-guide-kopf">
          <Coco pose="freut-sich" size={64} eager />
          <div className="stack">
            <h2 id="erste-schritte-fertig">{t("Alles eingerichtet", "All set up")}</h2>
            <p>{t("Ab jetzt laufen Rechnungen und Auszahlungen ohne Rückfragen.", "From now on invoices and payouts run without queries.")}</p>
          </div>
        </div>
        <div className="row"><button type="button" className="primary" onClick={() => { setGone(true); void save({ first_steps_celebrated: true }).catch(() => undefined); }}>{t("Weiter", "Continue")}</button></div>
      </section>
    );
  }
  if (!ready || gone || onboarding.first_steps_dismissed || done === steps.length) return null;
  const naechster = steps.find((s) => !s.done);
  return (
    <section className="panel stack tk-first-steps" aria-labelledby="erste-schritte" data-schreiben>
      <div className="row between">
        {/* Paket 2: Stand als grauer Zusatz neben dem Titel (wie „2 von 3 erledigt“ im Original) */}
        <h2 id="erste-schritte">{t("Erste Schritte", "First steps")} <span className="tk-steps-stand">{t(`${done} von ${steps.length} erledigt`, `${done} of ${steps.length} done`)}</span></h2>
        <button type="button" className="quiet" onClick={() => { setGone(true); void save({ first_steps_dismissed: true }).catch(() => undefined); }}>{t("Ausblenden", "Hide")}</button>
      </div>
      <p>{t("Einmal einrichten, dann laufen Rechnungen und Auszahlungen ohne Rückfragen.", "Set up once, then invoices and payouts run without queries.")}</p>
      <div className="progress" role="img" aria-label={t(`${done} von ${steps.length} Schritten erledigt`, `${done} of ${steps.length} steps done`)}>
        <span style={{ width: `${Math.round((done / steps.length) * 100)}%` }}></span>
      </div>
      <ol className="tk-steps">
        {steps.map((s, i) => (
          <li key={s.key} className={s.done ? "done" : s === naechster ? "naechster" : undefined}>
            <span className="tk-step-mark" aria-hidden="true">{s.done ? "✓" : i + 1}</span>
            <span className="tk-step-text">
              <strong>{s.label}</strong>
              {s.done ? <span className="sr-only"> · {t("erledigt", "done")}</span> : <small>{s.hint}</small>}
            </span>
            {/* D-2 (Inhaber 21.10.2026): nur der nächste offene Schritt trägt einen Knopf, die übrigen einen ruhigen Textlink */}
            {!s.done && (s === naechster
              ? <Link className="btn primary" to={s.to}>{s.action}</Link>
              : <Link className="tk-step-link" to={s.to}>{s.action} <span aria-hidden="true">→</span></Link>)}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Rundgang im Creator-Portal (20.10.2026): Übersicht, Deals, Rechnung einreichen, Verträge, Chat, Profil und Bankdaten. Startet
 * beim ersten Login auf der Übersicht von selbst, sonst über „Konto“ → „Rundgang starten“. Gespeichert wird beim Start, je Schritt
 * (K-591: nach dem Neuladen geht es dort weiter) und am Ende, gemessen werden Start, Ende und Überspringen (0380). Schweizer Schreibung gilt im Creator-Portal nicht (layouts.tsx).
 */
export function CocoRundgangCreator() {
  const { pathname } = useLocation();
  const ws = useTalentWorkspace();
  const { account, session } = useAccount();
  const userId = account?.session.userId ?? session?.userId ?? null;
  const t = useT();
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const lauf = useRundgang();
  const stationen = stationenFuer("creator");
  const embedded = typeof document !== "undefined" && document.documentElement.dataset.embed === "true";
  const auto = useRef(false);
  const gemeldet = useRef(0);
  // K-591: Speichern nacheinander (Schritt, dann Ende), nie überholend
  const kette = useRef<Promise<unknown>>(Promise.resolve());
  const speichern = (patch: Parameters<typeof save>[0]) => { kette.current = kette.current.then(() => save(patch)).catch(() => undefined); };
  useEffect(() => {
    if (auto.current || rundgangJetzt() || !ready || ws.archived) return;
    // K-591: nach dem Neuladen mitten im Rundgang beim gespeicherten Schritt weiter
    const weiterBei = fortsetzSchritt({ rolle: "creator", onboarding, anzahl: stationen.length, stationPfade: stationen.map((x) => x.pfad), pfad: pathname, jetzt: Date.now(), eingebettet: embedded });
    if (weiterBei !== null) {
      auto.current = true;
      rundgangStarten("creator", "resume", { schritt: weiterBei });
      return;
    }
    if (sollAutoStarten({ rolle: "creator", onboarding, pfad: pathname, eingebettet: embedded })) {
      auto.current = true;
      rundgangStarten("creator", "auto");
    }
  }, [ready, pathname]);
  // Stand je Schritt merken (K-591), den Start einmal je Lauf messen (nicht beim Fortsetzen)
  useEffect(() => {
    if (!lauf || lauf.rolle !== "creator") return;
    speichern(rundgangPatch(onboarding, "creator", stationen, "laufend", Math.min(lauf.schritt, stationen.length - 1), new Date().toISOString()));
    if (lauf.nr === gemeldet.current) return;
    gemeldet.current = lauf.nr;
    if (lauf.quelle !== "resume") void api.read("coco_tour_log", ereignisArgs(ws.org.org_id, "creator", "started", null, stationen.length)).catch(() => undefined);
  }, [lauf?.nr, lauf?.schritt]);
  useEffect(() => {
    if (lauf && lauf.rolle !== "creator") rundgangEnde();
  }, [lauf?.nr]);
  if (!lauf || lauf.rolle !== "creator" || ws.archived) return null;
  const index = Math.min(lauf.schritt, stationen.length - 1);
  const ende = (status: "beendet" | "uebersprungen", i: number) => {
    rundgangEnde();
    speichern(rundgangPatch(onboarding, "creator", stationen, status, i, new Date().toISOString()));
    void api.read("coco_tour_log", ereignisArgs(ws.org.org_id, "creator", status === "beendet" ? "finished" : "skipped", status === "beendet" ? stationen.length : i + 1, stationen.length)).catch(() => undefined);
  };
  return <RundgangBlase key={lauf.nr} stationen={stationen} index={index} t={t} text={(s) => stationText(s.id, t, { agency: ws.org.name })} onSchritt={rundgangSchritt} onEnde={ende} />;
}
